import * as THREE from "three";

export type InterpolationSink = ( object: THREE.Object3D, extrapolate: boolean ) => void;

class Track
{
  public readonly prev = new THREE.Vector3();
  public readonly prev_q = new THREE.Quaternion();
  public readonly curr = new THREE.Vector3();
  public readonly curr_q = new THREE.Quaternion();
  public readonly saved = new THREE.Vector3();
  public readonly saved_q = new THREE.Quaternion();
  public seen = 0;
  public detached = false;

  constructor( public readonly object: THREE.Object3D )
  {
    this.snap();
  }

  public snap(): void
  {
    this.curr.copy( this.object.position );
    this.curr_q.copy( this.object.quaternion );
    this.prev.copy( this.curr );
    this.prev_q.copy( this.curr_q );
  }
}

export class PhysicsInterpolation
{
  private static readonly MAX_JUMP_SQ = 9;
  private static readonly enabled = PhysicsInterpolation.readEnabled();

  private static provider: ( ( add: InterpolationSink ) => void ) | null = null;
  private static objects: THREE.Object3D[] = [];
  private static extrapolated: boolean[] = [];
  private static count = 0;
  private static readonly sink: InterpolationSink = ( object, extrapolate ) =>
  {
    PhysicsInterpolation.objects[PhysicsInterpolation.count] = object;
    PhysicsInterpolation.extrapolated[PhysicsInterpolation.count] = extrapolate;
    PhysicsInterpolation.count++;
  };
  private static tracks = new Map<THREE.Object3D, Track>();
  private static applied: Track[] = [];
  private static frame = 0;
  private static stepped = false;
  private static alpha = 0;

  public static setProvider( provider: ( ( add: InterpolationSink ) => void ) | null ): void
  {
    this.provider = provider;
    this.tracks.clear();
    this.objects.length = 0;
    this.extrapolated.length = 0;
  }

  public static onPhysicsFrame( steps: number, alpha: number ): void
  {
    this.stepped = steps > 0;
    this.alpha = Math.min( 1, Math.max( 0, alpha ) );
  }

  public static apply(): void
  {
    this.applied.length = 0;
    if ( !this.enabled || !this.provider )
      return;

    this.frame++;
    this.count = 0;
    this.provider( this.sink );

    for ( let i = 0; i < this.count; i++ )
    {
      const object = this.objects[i];
      const extrapolate = this.extrapolated[i];
      let track = this.tracks.get( object );
      if ( !track )
      {
        track = new Track( object );
        this.tracks.set( object, track );
      }
      else if ( track.seen !== this.frame - 1 )
        track.snap();
      else if ( this.stepped )
      {
        track.prev.copy( track.curr );
        track.prev_q.copy( track.curr_q );
        track.curr.copy( object.position );
        track.curr_q.copy( object.quaternion );
      }
      else if ( !track.curr.equals( object.position ) || !track.curr_q.equals( object.quaternion ) )
        track.snap();

      track.seen = this.frame;

      if ( track.prev.distanceToSquared( track.curr ) > this.MAX_JUMP_SQ )
        continue;

      track.saved.copy( object.position );
      track.saved_q.copy( object.quaternion );

      if ( extrapolate )
        object.position.lerpVectors( track.prev, track.curr, 1 + this.alpha );
      else
      {
        object.position.lerpVectors( track.prev, track.curr, this.alpha );
        object.quaternion.slerpQuaternions( track.prev_q, track.curr_q, this.alpha );
      }
      track.detached = !PhysicsInterpolation.inScene( object );
      if ( track.detached )
        object.updateMatrixWorld();
      this.applied.push( track );
    }

    this.objects.fill( null as unknown as THREE.Object3D, this.count );
    if ( this.frame % 120 === 0 )
      for ( const [ object, track ] of this.tracks )
        if ( track.seen !== this.frame )
          this.tracks.delete( object );
  }

  public static restore(): void
  {
    for ( const track of this.applied )
    {
      track.object.position.copy( track.saved );
      track.object.quaternion.copy( track.saved_q );
      if ( track.detached )
        track.object.updateMatrixWorld();
    }
    this.applied.length = 0;
  }

  private static inScene( object: THREE.Object3D ): boolean
  {
    let root = object;
    while ( root.parent )
      root = root.parent;
    return ( root as THREE.Scene ).isScene === true;
  }

  private static readEnabled(): boolean
  {
    try { return localStorage.getItem( "bb_phys_interp" ) !== "0"; } catch { return true; }
  }
}
